
import { computed, defineComponent, onBeforeMount, reactive } from 'vue'
import { useRoute, useRouter } from 'vue-router'

// @components
import Loader from '@/common/components/UI/Loader/index.vue'
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'

// @module/components
import CourierStatus from '@/modules/NewDispatcher/components/CourierStatus/index.vue'
import CourierSchedule from '@/modules/NewDispatcher/components/CourierSchedule/index.vue'
import CourierDriverLicenses from '@/modules/NewDispatcher/components/CourierDriverLicenses/index.vue'
import CourierIdentificationCards from '@/modules/NewDispatcher/components/CourierIdentificationCards/index.vue'
import SbHistory from '@/modules/NewDispatcher/components/SbHistory/index.vue'

// @types
import { Courier, CourierScheduleType, CourierPayload } from '@/new-services/types/couriers'
import { DispatcherSector } from '@/new-services/types/dispatcher-sectors'
import { CompanyT } from '@/core/types/companies.types'
import { CouirerCarT, CourierTypeT } from '@/core/types/couriers.types'

// @services
import couriersAPI from '@/new-services/couriers.service'
import dispatcherSectorsAPI from '@/new-services/dispatcher-sectors.service'

// @realisations
import { companies } from '@/core/realisations/companies.realisation'
import { couriers } from '@/core/realisations/couriers.realisation'

// @composable
import useNotifications from '@/common/composable/useNotifications'
import useAccount from '@/common/composable/useAccount'

// @constnants
import { CourierPaymentTypes } from '@/core/constants/couriers.constants'

// @validation
import useVuelidate from '@vuelidate/core'
import { decimal, required } from '@vuelidate/validators'

export default defineComponent({
    components: {
        Loader,
        PageTitle,
        TheButton,
        CourierStatus,
        TheInput,
        TheSelect,
        CourierDriverLicenses,
        CourierIdentificationCards,
        SbHistory,
        CourierSchedule,
    },
    setup() {
        const route = useRoute()
        const router = useRouter()
        const courier_id = Number(route.params.courier_id)

        const { havePermissions } = useAccount()
        const notifications = useNotifications()

        const state = reactive({
            is_loading: false,
            is_failed: false,
            //
            courier: null as Courier | null,
            //
            is_edit_mode: false,
            is_edit_loading: false,
            is_status_change_loading: false,
        })

        const edit_state = reactive({
            iin: null as string | null,
            fullName: null as string | null,
            phoneNumber: null as string | null,
            paymentRateType: null as number | null,
            paymentAmount: null as string | null,
            dispatcherSectorId: null as number | null,
            companyId: null as number | null,
            scheduleTypeId: null as number | null,
            carId: null as number | null,
            typeIds: [] as number[],
            code1C: null as string | null,
        })

        const edit_form$ = useVuelidate(
            {
                iin: { required },
                fullName: { required },
                phoneNumber: { required },
                paymentRateType: { required },
                paymentAmount: { required, decimal },
                dispatcherSectorId: { required },
                companyId: { required },
                scheduleTypeId: { required },
                carId: { required },
                typeIds: { required },
            },
            edit_state
        )

        const dispatcher_sectors_state = reactive({
            is_loading: false,
            is_failed: false,
            list: [] as DispatcherSector[],
        })

        const companies_state = reactive({
            is_loading: false,
            is_failed: false,
            list: [] as CompanyT[],
        })

        const schedule_types_state = reactive({
            is_loading: false,
            is_failed: false,
            list: [] as CourierScheduleType[],
        })

        const cars_state = reactive({
            is_loading: false,
            is_failed: false,
            list: [] as CouirerCarT[],
        })

        const types_state = reactive({
            is_loading: false,
            is_failed: false,
            list: [] as CourierTypeT[],
        })

        const loadDispatcherSectors = async () => {
            try {
                dispatcher_sectors_state.is_loading = true

                const response = await dispatcherSectorsAPI.getAllDispatcherSectors()
                dispatcher_sectors_state.list = response.data.data

                dispatcher_sectors_state.is_failed = false
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При получении списка секторов диспетчера произошла ошибка',
                })
                dispatcher_sectors_state.is_failed = true
            } finally {
                dispatcher_sectors_state.is_loading = false
            }
        }

        const loadCourierTypes = async () => {
            try {
                types_state.is_loading = true

                const response = await couriers.getCourierTypes()
                types_state.list = response.data

                types_state.is_failed = false
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При получении списка секторов диспетчера произошла ошибка',
                })
                types_state.is_failed = true
            } finally {
                types_state.is_loading = false
            }
        }

        const loadCompanies = async () => {
            try {
                companies_state.is_loading = true

                const response = await companies.getCompanies({
                    page: 1,
                    isExtended: 0,
                    limit: 9999,
                    getAll: 1,
                })
                companies_state.list = response.data

                companies_state.is_failed = false
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При получении списка компаний произошла ошибка',
                })
                companies_state.is_failed = true
            } finally {
                companies_state.is_loading = false
            }
        }

        const loadScheduleTypes = async () => {
            try {
                schedule_types_state.is_loading = true

                const response = await couriersAPI.getCourierScheduleTypes({})
                schedule_types_state.list = response.data.data

                schedule_types_state.is_failed = false
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При получении списка смен произошла ошибка',
                })
                schedule_types_state.is_failed = true
            } finally {
                schedule_types_state.is_loading = false
            }
        }

        const loadCars = async () => {
            try {
                cars_state.is_loading = true

                const response = await couriers.getCars({ page: 1, limit: 9999 })
                cars_state.list = response.data

                cars_state.is_failed = false
            } catch (error) {
                cars_state.is_failed = true
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При получении списка машин произошла ошибка',
                })
            } finally {
                cars_state.is_loading = false
            }
        }

        const initializeEditState = () => {
            if (!state.courier) return

            loadDispatcherSectors()
            loadCompanies()
            loadScheduleTypes()
            loadCars()
            loadCourierTypes()

            edit_state.iin = state.courier.iin
            edit_state.fullName = state.courier.fullName
            edit_state.phoneNumber = state.courier.phoneNumber
            edit_state.dispatcherSectorId = state.courier.dispatcherSector.id
            edit_state.companyId = state.courier.company.id
            edit_state.paymentAmount = state.courier.paymentAmount
            edit_state.typeIds = state.courier.types.map((type) => type.id)
            edit_state.code1C = state.courier.code1C

            if (state.courier.car) {
                edit_state.carId = state.courier.car.id
            }

            if (state.courier.schedule) {
                edit_state.scheduleTypeId = state.courier.schedule.id
            }

            if (state.courier.paymentRateType) {
                edit_state.paymentRateType = state.courier.paymentRateType.id
            }
        }

        const loadCourier = async () => {
            try {
                state.is_loading = true

                const response = await couriersAPI.getCourier(courier_id)
                state.courier = response.data.data

                initializeEditState()

                state.is_failed = false
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При получении данных о курьере произошла ошибка',
                })
                state.is_failed = true
            } finally {
                state.is_loading = false
            }
        }

        onBeforeMount(() => {
            loadCourier()

            if (route.query.edit) {
                state.is_edit_mode = true
            }
        })

        const toggleEdit = () => {
            state.is_edit_mode = !state.is_edit_mode
        }

        const updateCourier = async () => {
            edit_form$.value.$touch()
            if (edit_form$.value.$invalid) return

            try {
                state.is_edit_loading = true

                const payload: CourierPayload = {
                    iin: edit_state.iin as string,
                    fullName: edit_state.fullName as string,
                    phoneNumber: edit_state.phoneNumber as string,
                    paymentRateType: edit_state.paymentRateType as number,
                    paymentAmount: edit_state.paymentAmount as string,
                    dispatcherSectorId: edit_state.dispatcherSectorId as number,
                    companyId: edit_state.companyId as number,
                    scheduleTypeId: edit_state.scheduleTypeId as number,
                    carId: edit_state.carId as number,
                    typeIds: edit_state.typeIds,
                    code1C: edit_state.code1C as string,
                }

                await couriersAPI.updateCourier(courier_id, payload)

                toggleEdit()
                loadCourier()

                notifications.notify({ type: 'success', title: 'Изменения сохранены' })
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При редактировании курьера произошла ошибка',
                })
            } finally {
                state.is_edit_loading = false
            }
        }

        const onBack = () => {
            router.push('/new-dispatcher/couriers')
        }

        const isCanSetOnCheckStatus = computed(() => {
            return havePermissions(['couriers-status-in-checkup', 'all-root-permission'])
        })

        const isCourierOnCheck = computed(() => {
            return state.courier && state.courier.status.id === 1
        })

        const isCanSetActiveStatus = computed(() => {
            return havePermissions(['couriers-status-active', 'all-root-permission'])
        })

        const isCourierActive = computed(() => {
            return state.courier && state.courier.status.id === 2
        })

        const isCanSetArchiveStatus = computed(() => {
            return havePermissions(['couriers-status-archived', 'all-root-permission'])
        })

        const isCourierArchive = computed(() => {
            return state.courier && state.courier.status.id === 3
        })

        const updateStatus = (status_id: number, status_name: string) => {
            if (!state.courier) return

            state.courier.status.id = status_id
            state.courier.status.title = status_name
        }

        const setOnCheckStatus = async () => {
            if (state.is_status_change_loading || !state.courier) return

            try {
                state.is_status_change_loading = true

                await couriers.setCheckupStatus(state.courier.id)
                updateStatus(1, 'На проверке(СБ)')

                notifications.notify({ title: 'Статус изменен', type: 'success' })
            } catch (error) {
                console.error(error)
                notifications.notifyError({ error, title: 'При смене статуса произошла ошибка' })
            } finally {
                state.is_status_change_loading = false
            }
        }

        const setActiveStatus = async () => {
            if (state.is_status_change_loading || !state.courier) return

            try {
                state.is_status_change_loading = true

                await couriers.setActiveStatus(state.courier.id)
                updateStatus(2, 'Активный')

                notifications.notify({ title: 'Статус изменен', type: 'success' })
            } catch (error) {
                console.error(error)
                notifications.notifyError({ error, title: 'При смене статуса произошла ошибка' })
            } finally {
                state.is_status_change_loading = false
            }
        }

        const setArchiveStatus = async () => {
            if (state.is_status_change_loading || !state.courier) return

            try {
                state.is_status_change_loading = true

                await couriers.setArchiveStatus(state.courier.id)
                updateStatus(3, 'В архиве')

                notifications.notify({ title: 'Статус изменен', type: 'success' })
            } catch (error) {
                console.error(error)
                notifications.notifyError({ error, title: 'При смене статуса произошла ошибка' })
            } finally {
                state.is_status_change_loading = false
            }
        }

        const isRenderActiveStatusButton = computed(
            () => Boolean(state.courier) && !isCourierActive.value && isCanSetActiveStatus.value
        )
        const isRenderOnCheckStatusButton = computed(
            () => Boolean(state.courier) && !isCourierOnCheck.value && isCanSetOnCheckStatus.value
        )
        const isRenderArchiveStatusButton = computed(
            () => Boolean(state.courier) && !isCourierArchive.value && isCanSetArchiveStatus.value
        )

        const isCanReject = computed(() => {
            if (!state.courier) return false
            if (state.courier.status.id !== 1) return false
            if (!havePermissions(['couriers-status-reject', 'all-root-permission'])) return false

            return true
        })

        const reject_state = reactive({
            is_loading: false,
        })

        const rejectCourier = async () => {
            const answer = confirm('Вы действительно хотите отказать курьеру?')
            if (!answer) return

            try {
                reject_state.is_loading = true

                await couriers.rejectCourier(courier_id)

                notifications.notify({ title: 'Курьеру отказано', type: 'success' })
                loadCourier()
            } catch (error) {
                console.error(error)
                notifications.notifyError({ error, title: 'При отказе произошла ошибка' })
            } finally {
                reject_state.is_loading = false
            }
        }

        return {
            CourierPaymentTypes,
            courier_id,
            //
            state,
            edit_state,
            dispatcher_sectors_state,
            companies_state,
            schedule_types_state,
            cars_state,
            types_state,
            edit_form$,
            //
            loadDispatcherSectors,
            loadCompanies,
            loadScheduleTypes,
            loadCars,
            loadCourier,
            loadCourierTypes,
            toggleEdit,
            //
            updateCourier,
            onBack,
            //
            setOnCheckStatus,
            setActiveStatus,
            setArchiveStatus,
            isRenderActiveStatusButton,
            isRenderOnCheckStatusButton,
            isRenderArchiveStatusButton,
            isCanReject,
            reject_state,
            rejectCourier,
        }
    },
})
